/**
 * SHARED nautical-chart / parchment UI language.
 *
 * The game's signature look is the aged sea-chart the HUD draws in `renderChart` (apps/client/src/hud.ts):
 * warm sepia paper, an ink neat-line, a gilt rope + wood band + brass corner studs, Cinzel labels. Two DOM
 * menus (the shipyard hull cards in basepanel.ts and the Captain's Opportunities board in opportunities.ts)
 * used to be generic dark-glass / Material cards that clashed with it. This module is the single source of
 * that parchment language so every surface CASES pixel-for-pixel with the chart:
 *
 *  • `parchmentTexture()` — the EXACT aged-paper canvas the chart bakes (moved here verbatim; hud.ts now
 *    calls it too, so there is ONE generator, never a divergent copy).
 *  • `parchmentDataUrl()` — that texture as a CSS-usable data URL (memoised).
 *  • `injectParchmentCss()` — installs the `.pg-*` class kit (framed paper, ink cartouche, brass seal,
 *    engraved gauge) once, with the paper wired into `--pg-paper`.
 *
 * Zero wire, zero game logic — pure presentation. Canvas is generated once and blitted to a data URL, so it
 * costs the 3D PvP render budget nothing (it only paints inside a DOM menu, for one player).
 */

let _parch: HTMLCanvasElement | null = null;
let _dataUrl: string | null = null;
let _injected = false;

/** The aged-parchment TEXTURE, generated once: a warm sepia radial base, low-freq mottling, tea-stains,
 *  per-pixel grain, paper fibers, fold creases and burnt edges. Seeded so every load looks identical (brand
 *  consistency + reproducible screenshots). The per-pixel pass runs ONCE — after this it's just a blit.
 *  This is the chart's paper, verbatim; hud.ts blits it and the DOM menus reference it via a data URL. */
export function parchmentTexture(): HTMLCanvasElement {
  if (_parch) return _parch;
  const N = 640, cv = document.createElement('canvas'); cv.width = cv.height = N;
  const p = cv.getContext('2d')!;
  let s = 0x1a2b3c4d >>> 0; const rnd = (): number => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  const cl = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v);
  const g = p.createRadialGradient(N * 0.42, N * 0.38, N * 0.05, N * 0.5, N * 0.52, N * 0.75);
  g.addColorStop(0, '#efe1ba'); g.addColorStop(0.5, '#e4cf9b'); g.addColorStop(1, '#cdac71');
  p.fillStyle = g; p.fillRect(0, 0, N, N);
  for (let i = 0; i < 80; i++) { const x = rnd() * N, y = rnd() * N, r = 40 + rnd() * 150, dark = rnd() < 0.5, a = 0.035 + rnd() * 0.06;
    const rg = p.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, dark ? `rgba(120,90,46,${a})` : `rgba(255,246,216,${a})`); rg.addColorStop(1, 'rgba(0,0,0,0)');
    p.fillStyle = rg; p.beginPath(); p.arc(x, y, r, 0, Math.PI * 2); p.fill(); }
  for (let i = 0; i < 12; i++) { const x = rnd() < 0.5 ? rnd() * N * 0.3 : N - rnd() * N * 0.3, y = rnd() * N, r = 50 + rnd() * 130;
    const rg = p.createRadialGradient(x, y, r * 0.2, x, y, r);
    rg.addColorStop(0, 'rgba(104,68,28,.07)'); rg.addColorStop(0.7, 'rgba(120,80,34,.04)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
    p.fillStyle = rg; p.beginPath(); p.arc(x, y, r, 0, Math.PI * 2); p.fill(); }
  const img = p.getImageData(0, 0, N, N), dt = img.data;
  for (let i = 0; i < dt.length; i += 4) { const n = (rnd() - 0.5) * 22; dt[i] = cl(dt[i]! + n); dt[i + 1] = cl(dt[i + 1]! + n * 0.9); dt[i + 2] = cl(dt[i + 2]! + n * 0.7); }
  p.putImageData(img, 0, 0);
  for (let i = 0; i < 520; i++) { const x = rnd() * N, y = rnd() * N, a = rnd() * Math.PI, len = 4 + rnd() * 16, dk = rnd() < 0.5;
    p.strokeStyle = dk ? 'rgba(92,66,30,.05)' : 'rgba(255,248,222,.05)'; p.lineWidth = 1;
    p.beginPath(); p.moveTo(x, y); p.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len); p.stroke(); }
  const crease = (x0: number, y0: number, x1: number, y1: number, light: boolean): void => {
    const gg = p.createLinearGradient(x0, y0, x1, y1);
    gg.addColorStop(0, 'rgba(0,0,0,0)'); gg.addColorStop(0.5, light ? 'rgba(255,250,228,.10)' : 'rgba(70,48,20,.10)'); gg.addColorStop(1, 'rgba(0,0,0,0)');
    p.strokeStyle = gg; p.lineWidth = 3; p.beginPath(); p.moveTo(x0, y0); p.lineTo(x1, y1); p.stroke(); };
  crease(N * 0.34, 0, N * 0.34, N, false); crease(N * 0.346, 0, N * 0.346, N, true);
  crease(N * 0.7, 0, N * 0.7, N, false);
  crease(0, N * 0.52, N, N * 0.52, false); crease(0, N * 0.526, N, N * 0.526, true);
  const vg = p.createRadialGradient(N / 2, N / 2, N * 0.32, N / 2, N / 2, N * 0.72);
  vg.addColorStop(0, 'rgba(50,32,10,0)'); vg.addColorStop(0.78, 'rgba(50,32,10,.10)'); vg.addColorStop(1, 'rgba(34,20,6,.5)');
  p.fillStyle = vg; p.fillRect(0, 0, N, N);
  for (let i = 0; i < 28; i++) { const edge = Math.floor(rnd() * 4), t = rnd(); let x: number, y: number;
    if (edge === 0) { x = t * N; y = rnd() * N * 0.09; } else if (edge === 1) { x = t * N; y = N - rnd() * N * 0.09; }
    else if (edge === 2) { x = rnd() * N * 0.09; y = t * N; } else { x = N - rnd() * N * 0.09; y = t * N; }
    const r = 14 + rnd() * 34, rg = p.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, 'rgba(38,22,6,.34)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
    p.fillStyle = rg; p.beginPath(); p.arc(x, y, r, 0, Math.PI * 2); p.fill(); }
  _parch = cv; return cv;
}

/** The parchment texture as a data URL for CSS `background-image` — memoised (one encode). */
export function parchmentDataUrl(): string {
  if (_dataUrl) return _dataUrl;
  _dataUrl = parchmentTexture().toDataURL('image/png');
  return _dataUrl;
}

/**
 * The shared `.pg-*` class kit, authored to the chart's exact palette (wood #4a3016→#6b4522→#3a2410, gilt
 * rope #c8a05a + coils rgba(92,60,24,.6), brass studs #f0d48a→#c99a3a→#7a5416, ink neat-line rgba(58,40,16),
 * Cinzel labels). One element = one framed sheet; the paper rides `--pg-paper`.
 */
function pgCss(paper: string): string {
  return `
:root{ --pg-paper:url("${paper}"); }

/* ---------- framed parchment panel: wood band + gilt rope + brass corner studs (matches drawChartFrame) ---------- */
.pg-frame{ position:relative; border-radius:14px; padding:9px;
  background:
    radial-gradient(circle at 14px 14px, #f4da92 0%, #cba043 46%, #7a5416 72%, rgba(52,32,8,.95) 80%, transparent 85%) no-repeat,
    radial-gradient(circle at calc(100% - 14px) 14px, #f4da92 0%, #cba043 46%, #7a5416 72%, rgba(52,32,8,.95) 80%, transparent 85%) no-repeat,
    radial-gradient(circle at 14px calc(100% - 14px), #f4da92 0%, #cba043 46%, #7a5416 72%, rgba(52,32,8,.95) 80%, transparent 85%) no-repeat,
    radial-gradient(circle at calc(100% - 14px) calc(100% - 14px), #f4da92 0%, #cba043 46%, #7a5416 72%, rgba(52,32,8,.95) 80%, transparent 85%) no-repeat,
    linear-gradient(135deg,#4a3016 0%,#6b4522 50%,#3a2410 100%);
  box-shadow:inset 0 1px 0 rgba(180,130,70,.4), inset 0 0 0 1px rgba(20,10,2,.55), 0 14px 38px rgba(0,6,12,.62); }
/* gilt rope ring + its inked coils, just inside the wood band */
.pg-frame::before{ content:''; position:absolute; inset:5px; border-radius:9px; pointer-events:none; z-index:1;
  border:2px solid #c8a05a; box-shadow:0 0 0 1px rgba(58,40,16,.35); }
.pg-frame::after{ content:''; position:absolute; inset:5px; border-radius:9px; pointer-events:none; z-index:1;
  border:2px dashed rgba(92,60,24,.6); }

/* ---------- the aged parchment SHEET the content sits on (chart's paper + neat-line + vignette) ---------- */
.pg-paper{ position:relative; z-index:2; border-radius:7px; overflow:hidden; color:#3b2712;
  background:var(--pg-paper) center/cover, #e4cf9b;
  box-shadow:inset 0 0 0 1px rgba(58,40,16,.5), inset 0 0 0 2.5px rgba(58,40,16,.16), inset 0 0 36px rgba(70,44,18,.16), inset 0 0 120px rgba(34,20,6,.22); }
/* a lighter inset card (a chart-card pasted on the tome) — same paper, ink neat-line, tiny brass corner studs */
.pg-card{ position:relative; border-radius:6px; color:#3b2712;
  background:var(--pg-paper) center/cover, #e6d29d;
  box-shadow:inset 0 0 0 1px rgba(58,40,16,.5), inset 0 0 0 3px rgba(58,40,16,.14), inset 0 0 26px rgba(70,44,18,.14), 0 2px 6px rgba(40,24,6,.14); }
.pg-card::before, .pg-card::after{ content:''; position:absolute; width:5px; height:5px; border-radius:50%; pointer-events:none;
  background:radial-gradient(circle at 35% 32%, #f0d48a, #c99a3a 58%, #7a5416 100%); box-shadow:0 0 0 1px rgba(50,32,8,.7); }
.pg-card::before{ top:5px; left:5px; } .pg-card::after{ top:5px; right:5px; }

/* ---------- chart LABEL voice: inked Cinzel cartouche ---------- */
.pg-title{ font-family:'Cinzel',Georgia,serif; font-weight:900; letter-spacing:.16em; text-transform:uppercase;
  color:#5a3a12; text-shadow:0 1px 0 rgba(255,248,225,.6); }
.pg-sub{ font-family:'Cinzel',Georgia,serif; font-weight:700; letter-spacing:.08em; color:#6a4c22; }
/* ink neat-line divider (the chart's classic double border, as a rule) */
.pg-rule{ height:2px; border:0; margin:6px 2px; border-radius:1px;
  background:linear-gradient(90deg, rgba(58,40,16,0), rgba(58,40,16,.55) 12%, rgba(58,40,16,.55) 88%, rgba(58,40,16,0));
  box-shadow:0 2px 0 -1px rgba(58,40,16,.22); }

/* ---------- stamped aged-brass SEAL (a hull's role badge) ---------- */
.pg-seal{ display:inline-flex; align-items:center; gap:3px; font-family:'Cinzel',Georgia,serif; font-weight:700;
  font-size:9px; letter-spacing:.1em; text-transform:uppercase; color:#42280a; padding:2px 8px; border-radius:2px; white-space:nowrap;
  background:linear-gradient(180deg, rgba(222,186,102,.72), rgba(150,110,40,.5));
  border:1px solid rgba(90,60,16,.6); box-shadow:inset 0 1px 0 rgba(255,246,214,.55), inset 0 -1px 2px rgba(60,38,10,.3), 0 1px 2px rgba(40,24,6,.28); }

/* ---------- engraved GAUGE: inked trough + graduation ticks + ink-wash fill ---------- */
.pg-gauge{ position:relative; height:8px; border-radius:2px; overflow:hidden;
  background:rgba(74,52,24,.16); box-shadow:inset 0 0 0 1px rgba(58,40,16,.5), inset 0 1px 2px rgba(40,24,6,.4); }
.pg-gauge::after{ content:''; position:absolute; inset:0; pointer-events:none; z-index:2;
  background:repeating-linear-gradient(90deg, rgba(58,40,16,.32) 0 1px, transparent 1px 20%); opacity:.55; }
.pg-gauge > i{ position:relative; z-index:1; display:block; height:100%; border-radius:1px;
  box-shadow:inset 0 1px 0 rgba(255,248,220,.3), inset 0 -2px 3px rgba(40,24,6,.32), 1px 0 0 rgba(40,24,6,.4); }
`;
}

/** Install the shared parchment `.pg-*` classes + `--pg-paper` once (idempotent). Safe to call from any menu. */
export function injectParchmentCss(): void {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const st = document.createElement('style');
  st.id = 'pg-style';
  st.textContent = pgCss(parchmentDataUrl());
  document.head.appendChild(st);
}
